import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Save, Loader2, Palette, Mail, Globe, Upload } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn } from '@utils';
import api from '@services/api';
import type { Brand } from '@types';

const brandSchema = z.object({
  name: z.string().min(2, 'Brand name is required'),
  description: z.string().optional(),
  primary_color: z.string().optional(),
  secondary_color: z.string().optional(),
  accent_color: z.string().optional(),
  sender_name: z.string().optional(),
  sender_email: z.string().email().optional().or(z.literal('')),
  website_url: z.string().optional(),
});

type BrandFormData = z.infer<typeof brandSchema>;

export default function BrandSettingsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings-brand'],
    queryFn: () => api.get<{ data: Brand }>('/settings/brand'),
  });

  const brand = data?.data;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<BrandFormData>({
    resolver: zodResolver(brandSchema),
  });

  useEffect(() => {
    if (brand) {
      reset({
        name: brand.name || '',
        description: brand.description || '',
        primary_color: brand.primaryColor || '#3B82F6',
        secondary_color: brand.secondaryColor || '#10B981',
        accent_color: brand.accentColor || '#F59E0B',
        sender_name: brand.senderName || '',
        sender_email: brand.senderEmail || '',
        website_url: brand.websiteUrl || '',
      });
    }
  }, [brand, reset]);

  const onSubmit = async (data: BrandFormData) => {
    setIsSubmitting(true);
    try {
      await api.put('/settings/brand', data);
      toast.success('Brand settings saved successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save brand settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  const primaryColor = watch('primary_color') || '#3B82F6';
  const secondaryColor = watch('secondary_color') || '#10B981';
  const accentColor = watch('accent_color') || '#F59E0B';

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-40 dark:bg-gray-700" />
        <div className="h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-2xl mx-auto space-y-6"
    >
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Brand Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Customize your brand appearance and email settings</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Brand Info */}
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Brand Identity
          </h2>

          <div>
            <label htmlFor="name" className="label">Brand Name *</label>
            <input
              {...register('name')}
              type="text"
              id="name"
              className={cn('input', errors.name && 'input-error')}
            />
            {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>}
          </div>

          <div>
            <label htmlFor="description" className="label">Description</label>
            <textarea
              {...register('description')}
              id="description"
              rows={3}
              className="input"
              placeholder="Brief description of your brand..."
            />
          </div>

          <div>
            <label htmlFor="website_url" className="label">
              <Globe className="w-4 h-4 inline mr-1" />
              Website URL
            </label>
            <input
              {...register('website_url')}
              type="url"
              id="website_url"
              className="input"
              placeholder="https://..."
            />
          </div>
        </div>

        {/* Colors */}
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Brand Colors</h2>

          {/* Preview */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full border-2 border-white shadow" style={{ backgroundColor: primaryColor }} />
              <span className="text-xs text-gray-500">Primary</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full border-2 border-white shadow" style={{ backgroundColor: secondaryColor }} />
              <span className="text-xs text-gray-500">Secondary</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full border-2 border-white shadow" style={{ backgroundColor: accentColor }} />
              <span className="text-xs text-gray-500">Accent</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="primary_color" className="label">Primary</label>
              <div className="flex items-center gap-2">
                <input
                  {...register('primary_color')}
                  type="color"
                  id="primary_color"
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input {...register('primary_color')} type="text" className="input flex-1 text-sm" />
              </div>
            </div>
            <div>
              <label htmlFor="secondary_color" className="label">Secondary</label>
              <div className="flex items-center gap-2">
                <input
                  {...register('secondary_color')}
                  type="color"
                  id="secondary_color"
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input {...register('secondary_color')} type="text" className="input flex-1 text-sm" />
              </div>
            </div>
            <div>
              <label htmlFor="accent_color" className="label">Accent</label>
              <div className="flex items-center gap-2">
                <input
                  {...register('accent_color')}
                  type="color"
                  id="accent_color"
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                />
                <input {...register('accent_color')} type="text" className="input flex-1 text-sm" />
              </div>
            </div>
          </div>
        </div>

        {/* Email Settings */}
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Settings
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="sender_name" className="label">Sender Name</label>
              <input
                {...register('sender_name')}
                type="text"
                id="sender_name"
                className="input"
                placeholder="Your Organization"
              />
            </div>
            <div>
              <label htmlFor="sender_email" className="label">Sender Email</label>
              <input
                {...register('sender_email')}
                type="email"
                id="sender_email"
                className={cn('input', errors.sender_email && 'input-error')}
                placeholder="noreply@example.com"
              />
              {errors.sender_email && <p className="mt-1 text-sm text-danger-600">{errors.sender_email.message}</p>}
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Brand Settings
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
